/**
 * Feature Flags React Context
 * 
 * Provides React components with access to the feature flag system.
 * Automatically re-renders when features change.
 * 
 * Usage:
 *   // In your app root:
 *   import { FeatureProvider } from '@/core/features/FeatureContext';
 *   
 *   <FeatureProvider config={featureConfig}>
 *     <App />
 *   </FeatureProvider>
 *   
 *   // In any component:
 *   import { useFeature, useFeatureMode } from '@/core/features/FeatureContext';
 *   
 *   function MyComponent() {
 *     const showButton = useFeature('devtools', 'healButton');
 *     const isWhitelistMode = useFeatureMode('devtools') === FeatureMode.WHITELIST;
 *     
 *     return showButton ? <button>Heal</button> : null;
 *   }
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { featureFlags } from './FeatureFlagService';
import { FeatureMode, FeatureRegistry } from './FeatureTypes';

// =============================================================================
// CONTEXT
// =============================================================================

interface FeatureContextValue {
  /** Check if a feature is visible (triggers re-render on change) */
  isVisible: (domain: string, feature: string) => boolean;
  /** Get current mode for a domain */
  getMode: (domain: string) => FeatureMode;
  /** Get current whitelist for a domain */
  getWhitelist: (domain: string) => string[];
  /** Force refresh (rarely needed) */
  refresh: () => void;
}

const FeatureContext = createContext<FeatureContextValue | null>(null);

// =============================================================================
// PROVIDER
// =============================================================================

interface FeatureProviderProps {
  children: ReactNode;
  config: FeatureRegistry;
}

export const FeatureProvider: React.FC<FeatureProviderProps> = ({ children, config }) => {
  const [, forceUpdate] = useState({});

  // Initialize on mount
  useEffect(() => {
    featureFlags.init(config);
  }, []);

  // Subscribe to all feature changes
  useEffect(() => {
    const unsubscribe = featureFlags.onFeatureChange(() => {
      forceUpdate({});
    });
    return unsubscribe;
  }, []);

  const value: FeatureContextValue = {
    isVisible: useCallback((domain: string, feature: string) => {
      return featureFlags.isVisible(domain, feature);
    }, []),
    
    getMode: useCallback((domain: string) => {
      return featureFlags.getMode(domain);
    }, []),
    
    getWhitelist: useCallback((domain: string) => {
      return featureFlags.getWhitelist(domain);
    }, []),
    
    refresh: useCallback(() => {
      forceUpdate({});
    }, []),
  };

  return (
    <FeatureContext.Provider value={value}>
      {children}
    </FeatureContext.Provider>
  );
};

// =============================================================================
// HOOKS
// =============================================================================

/**
 * Hook to check if a feature is visible
 * Automatically re-renders when the feature changes
 * 
 * @example
 *   const showHeal = useFeature('devtools', 'healButton');
 *   return showHeal ? <HealButton /> : null;
 */
export function useFeature(domain: string, feature: string): boolean {
  const context = useContext(FeatureContext);
  
  if (!context) {
    // Fallback to direct service if outside provider
    return featureFlags.isVisible(domain, feature);
  }

  // Use state to trigger re-renders
  const [isVisible, setIsVisible] = useState(() => featureFlags.isVisible(domain, feature));

  useEffect(() => {
    // Update when anything changes
    const unsubscribe = featureFlags.onFeatureChange(() => {
      setIsVisible(featureFlags.isVisible(domain, feature));
    });
    return unsubscribe;
  }, [domain, feature]);

  return isVisible;
}

/**
 * Hook to get all visible features for a domain
 * 
 * @example
 *   const enabledFeatures = useEnabledFeatures('devtools');
 *   // ['healButton', 'manaButton', ...]
 */
export function useEnabledFeatures(domain: string): string[] {
  const [features, setFeatures] = useState(() => featureFlags.getEnabledFeatures(domain));

  useEffect(() => {
    const unsubscribeFeature = featureFlags.onFeatureChange(() => {
      setFeatures(featureFlags.getEnabledFeatures(domain));
    });
    const unsubscribeMode = featureFlags.onModeChange(() => {
      setFeatures(featureFlags.getEnabledFeatures(domain));
    });
    const unsubscribeWhitelist = featureFlags.onWhitelistChange(() => {
      setFeatures(featureFlags.getEnabledFeatures(domain));
    });

    return () => {
      unsubscribeFeature();
      unsubscribeMode();
      unsubscribeWhitelist();
    };
  }, [domain]);

  return features;
}

/**
 * Hook to get current mode for a domain
 * 
 * @example
 *   const mode = useFeatureMode('devtools');
 *   const isWhitelist = mode === FeatureMode.WHITELIST;
 */
export function useFeatureMode(domain: string): FeatureMode {
  const [mode, setMode] = useState(() => featureFlags.getMode(domain));

  useEffect(() => {
    const unsubscribe = featureFlags.onModeChange((event) => {
      if (event.domain === domain) {
        setMode(event.newMode);
      }
    });
    return unsubscribe;
  }, [domain]);

  return mode;
}

/**
 * Hook to get current whitelist for a domain
 * 
 * @example
 *   const whitelist = useFeatureWhitelist('devtools');
 *   // ['healButton', 'manaButton']
 */
export function useFeatureWhitelist(domain: string): string[] {
  const [whitelist, setWhitelist] = useState(() => featureFlags.getWhitelist(domain));

  useEffect(() => {
    const unsubscribe = featureFlags.onWhitelistChange((event) => {
      if (event.domain === domain) {
        setWhitelist(event.newWhitelist);
      }
    });
    return unsubscribe;
  }, [domain]);

  return whitelist;
}

/**
 * Hook to check if domain is in whitelist mode
 * 
 * @example
 *   const isTesting = useIsWhitelistMode('devtools');
 *   return isTesting ? <TestIndicator /> : null;
 */
export function useIsWhitelistMode(domain: string): boolean {
  const mode = useFeatureMode(domain);
  return mode === FeatureMode.WHITELIST;
}

/**
 * Hook to get feature flag actions (toggle, whitelist, etc)
 * 
 * @example
 *   const { toggle, whitelist } = useFeatureActions();
 *   <button onClick={() => toggle('devtools', 'healButton')}>
 */
export function useFeatureActions() {
  return {
    toggle: featureFlags.toggle.bind(featureFlags),
    setEnabled: featureFlags.setEnabled.bind(featureFlags),
    setMode: featureFlags.setMode.bind(featureFlags),
    whitelist: featureFlags.whitelist.bind(featureFlags),
    unwhitelist: featureFlags.unwhitelist.bind(featureFlags),
    setWhitelist: featureFlags.setWhitelist.bind(featureFlags),
    clearWhitelist: featureFlags.clearWhitelist.bind(featureFlags),
  };
}

/**
 * Hook to get all available domains
 */
export function useFeatureDomains(): string[] {
  const [domains, setDomains] = useState(() => featureFlags.getDomains());

  useEffect(() => {
    // Refresh when initialized
    setDomains(featureFlags.getDomains());
  }, []);

  return domains;
}

// =============================================================================
// HIGHER-ORDER COMPONENT
// =============================================================================

interface WithFeatureOptions {
  /** Domain to check */
  domain: string;
  /** Feature to check */
  feature: string;
  /** Component to render when feature is hidden */
  fallback?: React.ComponentType;
}

/**
 * HOC to conditionally render a component based on feature flag
 * 
 * @example
 *   const HealButton = withFeature(HealButtonBase, {
 *     domain: 'devtools',
 *     feature: 'healButton',
 *     fallback: HiddenButton
 *   });
 */
export function withFeature<P extends object>(
  Component: React.ComponentType<P>,
  options: WithFeatureOptions
): React.FC<P> {
  const { domain, feature, fallback: Fallback } = options;

  return function FeatureWrappedComponent(props: P) {
    const isVisible = useFeature(domain, feature);

    if (!isVisible) {
      return Fallback ? <Fallback {...props} /> : null;
    }

    return <Component {...props} />;
  };
}

// =============================================================================
// CONDITIONAL RENDER COMPONENT
// =============================================================================

interface FeatureGateProps {
  domain: string;
  feature: string;
  children: ReactNode;
  fallback?: ReactNode;
}

/**
 * Component for conditional rendering based on feature flag
 * 
 * @example
 *   <FeatureGate domain="devtools" feature="healButton">
 *     <HealButton />
 *   </FeatureGate>
 *   
 *   <FeatureGate 
 *     domain="devtools" 
 *     feature="healButton"
 *     fallback={<span>Heal disabled</span>}
 *   >
 *     <HealButton />
 *   </FeatureGate>
 */
export const FeatureGate: React.FC<FeatureGateProps> = ({
  domain,
  feature,
  children,
  fallback = null,
}) => {
  const isVisible = useFeature(domain, feature);
  return isVisible ? <>{children}</> : <>{fallback}</>;
};

// =============================================================================
// WHITELIST MODE INDICATOR
// =============================================================================

interface WhitelistIndicatorProps {
  domain: string;
  className?: string;
}

/**
 * Shows a visual indicator when domain is in whitelist mode
 * Useful for letting users know they're in testing mode
 */
export const WhitelistIndicator: React.FC<WhitelistIndicatorProps> = ({
  domain,
  className,
}) => {
  const isWhitelistMode = useIsWhitelistMode(domain);
  const whitelist = useFeatureWhitelist(domain);

  if (!isWhitelistMode) return null;

  return (
    <div
      className={className}
      style={{
        background: 'rgba(241, 196, 15, 0.2)',
        border: '1px solid rgba(241, 196, 15, 0.5)',
        borderRadius: '4px',
        padding: '8px 12px',
        fontSize: '11px',
        fontFamily: 'monospace',
        color: '#f1c40f',
        marginBottom: '12px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span>🧪</span>
        <span>WHITELIST MODE</span>
        <span style={{ opacity: 0.7 }}>|</span>
        <span>Showing: [{whitelist.join(', ') || 'none'}]</span>
      </div>
    </div>
  );
};
